import React, { useState, useEffect, useCallback } from "react";
import MobileEditModal from "./MobileEditModal";
import Calendar from "react-calendar";

const MobileSearchModal = ({ onClose, user }) => {
  const [reservations, setReservations] = useState([]);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [allTables, setAllTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    fetch("/reservations/api/tables/")
      .then(res => res.json())
      .then(data => setAllTables(
        (data.tables || []).sort((a, b) => a.label.localeCompare(b.label))
      ));
  }, []);

  const fetchReservations = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (tableFilter) params.append("table", tableFilter);
    if (dateFilter) params.append("date", dateFilter);

    fetch(`/reservations/api/reservations/all/?${params}`)
      .then(res => res.json())
      .then(data => {
        setReservations(data.reservations || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [search, tableFilter, dateFilter]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const now = new Date();
  const todayStr = now.toLocaleDateString("en-CA");

  const isPast = (r) => {
    if (r.reservation_date_raw !== todayStr || !r.start_datetime) return false;
    const [h, m] = r.start_datetime.split(":").map(Number);
    return (h * 60 + m) < (now.getHours() * 60 + now.getMinutes());
  };

  const getPeriodColor = (time) => {
    if (!time) return "bg-gray-700";
    const [h] = time.split(":").map(Number);
    if (h >= 8 && h < 12) return "bg-blue-800";
    if (h >= 12 && h < 20) return "bg-yellow-700";
    return "bg-red-900";
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">

      {/* Header */}
      <div className="bg-gray-900 border-b border-[#c1b428] px-4 py-3 flex justify-between items-center">
        <h2 className="text-[#c1b428] font-bold text-base">Pretraga rezervacija</h2>
        <button onClick={onClose} className="text-gray-400 text-2xl leading-none">✕</button>
      </div>

      {/* Filteri */}
      <div className="bg-gray-900 border-b border-gray-700 px-4 py-3 space-y-2">

        {/* Pretraga po imenu */}
        <div className="relative">
          <input
            type="text"
            placeholder="Pretraži po imenu gosta..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2.5 text-sm pr-8"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
            >
              ✕
            </button>
          )}
        </div>

        {/* Datum i stol u redu */}
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="w-full bg-gray-800 text-left border border-gray-600 rounded-lg px-2 py-2.5 text-xs flex items-center gap-1"
            >
              <span>{dateFilter ? dateFilter : <span className="text-gray-500">📅 Datum</span>}</span>
              {dateFilter && (
                <span
                  onClick={e => { e.stopPropagation(); setDateFilter(""); }}
                  className="ml-auto text-gray-400"
                >✕</span>
              )}
            </button>
            {showDatePicker && (
              <div className="absolute top-full left-0 z-10 mt-1">
                <Calendar
                  onChange={(date) => {
                    const str = date.toLocaleDateString("en-CA");
                    setDateFilter(str);
                    setShowDatePicker(false);
                  }}
                  value={dateFilter ? new Date(dateFilter + "T12:00:00") : new Date()}
                  className="react-calendar text-[#c1b428] bg-gray-800 border border-gray-600 rounded-lg"
                />
              </div>
            )}
          </div>
          <select
            value={tableFilter}
            onChange={e => setTableFilter(e.target.value)}
            className="bg-gray-800 text-[#c1b428] border border-gray-600 rounded-lg px-2 py-2.5 text-xs"
          >
            <option value="">Svi stolovi</option>
            {allTables.map(t => (
              <option key={t.id} value={t.label}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {loading ? (
          <p className="text-center text-gray-400 py-8 text-sm">Učitavam...</p>
        ) : reservations.length > 0 ? (
          reservations.map(r => {
            const past = isPast(r);
            return (
              <div
                key={r.id}
                className={`rounded-xl border p-3 ${
                  past ? "bg-gray-900 border-gray-700 opacity-40" : "bg-gray-900 border-gray-700"
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-black font-bold px-2 py-0.5 rounded text-xs ${
                    past ? "bg-gray-500" : "bg-[#c1b428]"
                  }`}>
                    {r.tables.join(", ")}
                  </span>
                  <span className="text-gray-400 text-xs">{r.reservation_date}</span>
                </div>
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold text-sm">{r.guest_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded text-white ${getPeriodColor(r.start_datetime)}`}>
                    {r.start_datetime} – {r.end_datetime}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">
                    👥 {r.guests}
                  </span>

                  {["moderator", "admin"].includes(user?.role) && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setSelectedReservation(r)}
                        className="p-1 bg-gray-700 text-[#c1b428] rounded text-xs hover:bg-gray-600"
                        title="Uredi"
                      >
                        ✏️
                      </button>

                      <button
                        onClick={() => setConfirmDelete(r)}
                        className="p-1 bg-gray-700 text-red-400 rounded text-xs hover:bg-red-900"
                        title="Obriši"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
                {r.note && (
                  <div className="mt-1 text-xs text-gray-400 italic">📝 {r.note}</div>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-center text-gray-400 py-8 text-sm">Nema rezervacija.</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-900 border-t border-gray-700">
        <div className="text-center text-xs text-gray-400 mb-2">
          {reservations.length} rezervacija
        </div>
        <button
          onClick={onClose}
          className="w-full py-3 bg-gray-700 text-[#c1b428] rounded-lg font-semibold text-sm"
        >
          Zatvori
        </button>
      </div>
      {/* Edit modal */}
      {selectedReservation && (
        <MobileEditModal
          reservation={selectedReservation}
          tables={allTables}
          onClose={() => setSelectedReservation(null)}
          onSuccess={() => {
            setSelectedReservation(null);
            fetchReservations();
          }}
        />
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-gray-900 border border-[#c1b428] rounded-xl p-6 w-80 text-center">
            <div className="text-3xl mb-3">🗑️</div>

            <h3 className="text-[#c1b428] font-bold text-sm mb-2">
              Brisanje rezervacije
            </h3>

            <p className="text-gray-300 text-xs mb-5">
              Obrisati rezervaciju za{" "}
              <span className="text-[#c1b428] font-bold">
                {confirmDelete.guest_name}
              </span>
              ?
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-700 text-[#c1b428] rounded text-xs font-semibold"
              >
                Odustani
              </button>

              <button
                onClick={async () => {
                  await fetch(
                    `/reservations/api/reservations/${confirmDelete.id}/delete/`,
                    { method: "DELETE" }
                  );

                  setConfirmDelete(null);
                  fetchReservations();
                }}
                className="px-4 py-2 bg-red-700 text-white rounded text-xs font-bold"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSearchModal;